import React, { useMemo, useState } from 'react'
import {
  Building2,
  CheckCircle,
  Clock,
  Globe,
  Landmark,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldEllipsis,
  ShieldQuestion,
  ShieldX,
  UserPlus,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { Button, SectionLoader } from '@/components/atoms'
import { PaginationWrapper } from '@/components/organisms'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useOrgKybStatus } from '@/hooks/useKyb'
import {
  useInviteMember,
  useMyOrgRole,
  useMyPrimaryOrganization,
  useOrgInquirySummary,
  useOrgInvitations,
  useOrgMembers,
  useRemoveMember,
  useRevokeInvitation,
  useUpdateMemberRole,
} from '@/hooks/useOrganization'
// Pure data, no JSX: the seven legal forms are declared once, next to the
// wizard field that also uses them, so this card cannot drift from the values
// the backend validates against.
import { LEGAL_FORMS } from '@/screens/attestation/institutional/institutionalFields'
import { authService } from '@/services/authService'
import {
  KybForbiddenError,
  KybLevelUnresolvedError,
  kybService,
} from '@/services/kybService'
import { organizationService } from '@/services/organizationService'
import { cn, formatDate } from '@/utils'
import type { KybLinkResponse } from '@/types/kyb'
import type { OrgInvitation, OrgMembership, OrgRole } from '@/types/organization'
import InviteMemberModal from './InviteMemberModal'
import OrgInvitationsList from './OrgInvitationsList'
import { MemberInquiriesPanel } from './MemberInquiries'
import { OrgMembersList, type OrgMemberRow } from './OrgMembersList'
import {
  DomainStatusBadge,
  KybStatusBadge,
  ORG_CARD,
  ORG_CARD_PADDING_DESKTOP,
  ORG_CARD_PADDING_MOBILE,
  ORG_HEADING,
  ORG_LABEL,
  ORG_SELECT_CONTENT,
  ORG_SELECT_ITEM,
  ORG_SELECT_TRIGGER,
  ORG_SUBHEADING,
  ORG_SURFACE,
  OrgEmptyState,
  OrgErrorBanner,
  RoleBadge,
  emailDomainState,
  isCurrentUserMembership,
  kybApplicantComment,
  kybState,
  memberName,
} from './OrgPrimitives'

interface OrganizationSectionProps {
  isMobile: boolean
}

/** Members per page. The rollup endpoint clamps `limit` to [1, 50]. */
const MEMBERS_PAGE_LIMIT = 25

/**
 * The Organization tab of Profile & Settings.
 *
 * Two views off one component, switched entirely by the viewer's org role:
 *
 *  - OWNER / ADMIN — members with per-member bid/RFQ counts, pending invitations,
 *    invite / change-role / remove controls, and a drill-down into any member's
 *    inquiries.
 *  - MEMBER — the roster only: names, emails and roles, read-only. No counts, no
 *    invitations, no controls, no drill-down.
 *
 * The role comes from `useMyOrgRole`, and every manager-only query is gated on it
 * with `enabled` so a plain MEMBER never fires a request the API would 403.
 */
export const OrganizationSection: React.FC<OrganizationSectionProps> = ({ isMobile }) => {
  const cardPadding = isMobile ? ORG_CARD_PADDING_MOBILE : ORG_CARD_PADDING_DESKTOP

  const {
    organization,
    orgId,
    isLoading: orgLoading,
    error: orgError,
    refetch: refetchOrganizations,
  } = useMyPrimaryOrganization()
  const { role, isLoading: roleLoading, canManage, canChangeRoles, canInviteOwner } =
    useMyOrgRole(orgId)

  const roleResolved = !!orgId && !roleLoading && !!role

  // Local UI state
  const [membersOffset, setMembersOffset] = useState(0)
  const [selectedMember, setSelectedMember] = useState<OrgMembership | null>(null)
  const [isInviteOpen, setInviteOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<OrgMembership | null>(null)
  const [inviteToRevoke, setInviteToRevoke] = useState<OrgInvitation | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)

  // KYB card state. Separate from `actionError` so a failed verification action
  // reports itself inside the card that caused it, not in the page-level banner.
  const [kybLink, setKybLink] = useState<KybLinkResponse | null>(null)
  const [kybMinting, setKybMinting] = useState(false)
  const [kybChecking, setKybChecking] = useState(false)
  const [kybActionError, setKybActionError] = useState<string | null>(null)
  const [kybForbidden, setKybForbidden] = useState(false)
  const [legalFormNeeded, setLegalFormNeeded] = useState(false)
  const [legalForm, setLegalForm] = useState('')
  const [savingLegalForm, setSavingLegalForm] = useState(false)

  const flashSuccess = (message: string) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  // ---- Data -----------------------------------------------------------------
  // Managers read the members list *through* the activity rollup, so the counts
  // arrive in the same request. Everyone else reads the plain members endpoint.
  const summaryParams = useMemo(
    () => ({ limit: MEMBERS_PAGE_LIMIT, offset: membersOffset }),
    [membersOffset],
  )
  const {
    summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useOrgInquirySummary(orgId, summaryParams, roleResolved && canManage)
  const {
    members,
    isLoading: membersLoading,
    error: membersError,
  } = useOrgMembers(orgId, roleResolved && !canManage)

  // Business verification. Readable by any member of the org — the *actions*
  // are manager-only, the status is not — so this is not gated on `canManage`.
  // `enabled` still waits for an org id, and the hook does not retry: these
  // endpoints answer 403 by design and a retry can never turn that into a 200.
  const {
    kybStatus,
    isLoading: kybLoading,
    error: kybError,
    refetch: refetchKyb,
  } = useOrgKybStatus(orgId, !!orgId)

  const { invitations, isLoading: invitationsLoading } = useOrgInvitations(
    orgId,
    roleResolved && canManage,
  )
  const pendingInvitations = useMemo(
    () => invitations.filter((invitation) => invitation.status === 'PENDING'),
    [invitations],
  )

  const currentUser = authService.getUser()

  const rows: OrgMemberRow[] = useMemo(() => {
    if (canManage) {
      return (summary?.data ?? []).map((row) => ({
        membership: row,
        counts: row.counts,
        isSelf: isCurrentUserMembership(row, currentUser),
      }))
    }
    return members.map((membership) => ({
      membership,
      // Activity is manager-only; a MEMBER must not see anyone's numbers.
      counts: null,
      isSelf: isCurrentUserMembership(membership, currentUser),
    }))
  }, [canManage, summary, members, currentUser])

  const listLoading = canManage ? summaryLoading : membersLoading
  const listError = canManage ? summaryError : membersError

  const membersTotal = canManage ? summary?.total ?? rows.length : rows.length
  const totalPages = canManage ? Math.ceil(membersTotal / MEMBERS_PAGE_LIMIT) : 1
  const currentPage = Math.floor(membersOffset / MEMBERS_PAGE_LIMIT) + 1

  // ---- Mutations ------------------------------------------------------------
  const inviteMutation = useInviteMember(orgId)
  const revokeMutation = useRevokeInvitation(orgId)
  const roleMutation = useUpdateMemberRole(orgId)
  const removeMutation = useRemoveMember(orgId)

  const errorMessage = (error: unknown, fallback: string) =>
    error instanceof Error && error.message ? error.message : fallback

  const handleInvite = async (values: { email: string; role: OrgRole }) => {
    setActionError(null)
    try {
      await inviteMutation.mutateAsync(values)
      setInviteOpen(false)
      flashSuccess(`Invitation sent to ${values.email}.`)
    } catch {
      // Surfaced inside the modal via `inviteMutation.error`.
    }
  }

  const handleChangeRole = async (row: OrgMemberRow, nextRole: OrgRole) => {
    if (nextRole === row.membership.role) return
    setActionError(null)
    setPendingUserId(row.membership.user_id)
    try {
      await roleMutation.mutateAsync({ userId: row.membership.user_id, role: nextRole })
      flashSuccess(`${memberName(row.membership)} is now ${nextRole.toLowerCase()}.`)
    } catch (error) {
      // e.g. "Cannot demote the last owner" — the server message is the useful one.
      setActionError(errorMessage(error, 'Failed to update role'))
    } finally {
      setPendingUserId(null)
    }
  }

  const handleRemoveConfirmed = async () => {
    if (!memberToRemove) return
    setActionError(null)
    setPendingUserId(memberToRemove.user_id)
    try {
      await removeMutation.mutateAsync(memberToRemove.user_id)
      if (selectedMember?.user_id === memberToRemove.user_id) setSelectedMember(null)
      flashSuccess(`${memberName(memberToRemove)} was removed.`)
      setMemberToRemove(null)
    } catch (error) {
      setActionError(errorMessage(error, 'Failed to remove member'))
      setMemberToRemove(null)
    } finally {
      setPendingUserId(null)
    }
  }

  const handleRevokeConfirmed = async () => {
    if (!inviteToRevoke) return
    setActionError(null)
    try {
      await revokeMutation.mutateAsync(inviteToRevoke.id)
      flashSuccess(`Invitation to ${inviteToRevoke.email} revoked.`)
    } catch (error) {
      setActionError(errorMessage(error, 'Failed to revoke invitation'))
    } finally {
      setInviteToRevoke(null)
    }
  }

  // ---- KYB actions ----------------------------------------------------------
  /**
   * Mint a hosted verification link — the start/resume path.
   *
   * The link is single-use and expires, so it is minted on demand and never
   * stored. The two structured failures are recoveries, not errors: a 409 means
   * the org has no `legal_form` for the backend to pick a verification level
   * from (collected below), and a 403 means the viewer may read this status but
   * not drive it.
   */
  const handleStartKyb = async () => {
    if (!orgId) return
    setKybMinting(true)
    setKybActionError(null)
    try {
      const result = await kybService.createLink(orgId)
      setKybLink(result)
    } catch (error) {
      if (error instanceof KybLevelUnresolvedError) {
        setLegalFormNeeded(true)
        return
      }
      if (error instanceof KybForbiddenError) {
        // A deliberate refusal. Never a retryable error — retrying cannot help.
        setKybForbidden(true)
        return
      }
      setKybActionError(errorMessage(error, 'Could not create a verification link'))
    } finally {
      setKybMinting(false)
    }
  }

  /**
   * The 409 `kyb_level_unresolved` recovery.
   *
   * `legal_form` shipped after these organizations were created, so every org
   * that predates it has none and the first "Start verification" click 409s.
   * Collecting it here keeps the recovery in the same card as the failure. The
   * backend refuses this write only while an active verification exists — and
   * the 409 says none was ever created — so it is always permitted at this
   * point.
   */
  const handleSaveLegalForm = async () => {
    if (!orgId || !legalForm) return
    setSavingLegalForm(true)
    setKybActionError(null)
    try {
      await organizationService.updateOrganization(orgId, { legal_form: legalForm })
      setLegalFormNeeded(false)
      // This tab's copy of the profile came from the org list query; refresh it
      // so a later render does not still show the pre-save organization.
      await refetchOrganizations()
      await handleStartKyb()
    } catch (error) {
      setKybActionError(errorMessage(error, 'Could not save the legal form'))
    } finally {
      setSavingLegalForm(false)
    }
  }

  /**
   * User-driven refresh, and the only way this card ever updates.
   * 🔴 Nothing in this app polls. Do not add an interval here.
   */
  const handleRecheckKyb = async () => {
    setKybChecking(true)
    setKybActionError(null)
    // The minted link is single-use. Once the user has been away and come back
    // to check, holding on to it would offer a URL that is probably spent.
    setKybLink(null)
    try {
      await refetchKyb()
    } finally {
      setKybChecking(false)
    }
  }

  const toggleActivity = (row: OrgMemberRow) => {
    setSelectedMember((current) =>
      current?.user_id === row.membership.user_id ? null : row.membership,
    )
  }

  // ---- Render ---------------------------------------------------------------
  if (orgLoading) {
    return <SectionLoader text="Loading organization..." />
  }

  if (!organization) {
    return (
      <div className={cn(ORG_CARD, cardPadding)}>
        <OrgEmptyState
          icon={Building2}
          title="No organization yet"
          description={
            orgError ??
            'You are not a member of an organization. Institutional accounts get one at signup, or you can join an existing one from an invitation link.'
          }
          className="py-6"
        />
      </div>
    )
  }

  const profile = organization.institutional_profile
  const hasBankingOnFile = !!profile?.bank_account_number_set || !!profile?.bank_routing_number_set

  const domainState = emailDomainState(organization)
  const DomainIcon =
    domainState === 'verified' ? ShieldCheck : domainState === 'unverified' ? ShieldAlert : Globe
  const domainCopy = {
    none: {
      title: 'No email domain',
      description:
        'This organization has not claimed an email domain. Claiming one and verifying it lets colleagues at that domain be recognised as part of your organization.',
      iconClass: 'text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]',
    },
    unverified: {
      title: organization.email_domain ?? '',
      description:
        'This domain has been claimed but not yet confirmed. Verification finishes when someone at the domain opens the link in the verification email — until then it grants nothing.',
      iconClass: 'text-[#c26c00] dark:text-[#ffa733]',
    },
    verified: {
      title: organization.email_domain ?? '',
      description: organization.email_domain_verified_at
        ? `Control of this domain was confirmed on ${formatDate(organization.email_domain_verified_at)}.`
        : 'Control of this domain has been confirmed.',
      iconClass: 'text-[#188f36] dark:text-[#3fd465]',
    },
  }[domainState]

  // ---- KYB card -------------------------------------------------------------
  // 🔴 Six distinct states, never collapsed. `pending_review` in particular must
  // not read like `approved`: a review that is still open has proved nothing.
  // Each state below differs by badge label, badge colour, icon and sentence.
  //
  // 🔴 The only comment field that may ever be rendered is `moderation_comment`,
  // and it is read through `kybApplicantComment` rather than off the status
  // object — see the note on that helper. Sumsub's `clientComment` is internal
  // and never leaves the backend.
  const kybCardState = kybState(kybStatus?.kyb_status)
  const kybComment = kybApplicantComment(kybStatus, kybCardState)
  // A failed read tells us nothing about the organization, so it gets its own
  // neutral presentation instead of borrowing any state's styling.
  const kybUnavailable = !kybLoading && !!kybError

  const neutralIconClass = 'text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]'

  const KybIcon = kybUnavailable
    ? ShieldQuestion
    : {
        none: Shield,
        in_progress: ShieldEllipsis,
        pending_review: Clock,
        retry: ShieldAlert,
        approved: ShieldCheck,
        rejected: ShieldX,
      }[kybCardState]

  const kybCopy = {
    none: {
      description:
        'Business verification (KYB) has not been started for this organization. It confirms the company exists and who controls it.',
      iconClass: neutralIconClass,
    },
    in_progress: {
      description:
        'Verification was started but not finished. Resume it to continue where it was left — nothing is submitted for review until every step is done.',
      iconClass: 'text-[#c26c00] dark:text-[#ffa733]',
    },
    pending_review: {
      description:
        'Everything has been submitted and our verification partner is reviewing it. Nothing is required from you, and this is not an approval — check back for the result.',
      iconClass: 'text-[#2563eb] dark:text-[#7aa7f7]',
    },
    retry: {
      description:
        'Our verification partner needs something corrected before the review can finish. Resume verification to fix and resubmit it.',
      iconClass: 'text-[#d74404] dark:text-[#ff8a5c]',
    },
    approved: {
      // Says what the approval covers, on purpose: an approved KYB is not the
      // same thing as an open account. The access gate reads the attestation,
      // not this, and the two can be out of step for a short while.
      description: kybStatus?.reviewed_at
        ? `This organization passed business verification on ${formatDate(kybStatus.reviewed_at)}. Approval covers the business check only; any other outstanding onboarding step is tracked separately.`
        : 'This organization passed business verification. Approval covers the business check only; any other outstanding onboarding step is tracked separately.',
      iconClass: 'text-[#188f36] dark:text-[#3fd465]',
    },
    rejected: {
      description:
        'Our verification partner could not verify this organization, and the decision is final on this application. Contact support if you believe it is a mistake.',
      // A shade stronger than `retry` above, to match the filled badge: the two
      // failures must not read as the same failure.
      iconClass: 'text-[#d74404] dark:text-[#ff540c]',
    },
  }[kybCardState]

  // Actions are manager-only. A MEMBER inherits the org's status but the
  // link endpoint answers 403, so the button is never rendered for one — and a
  // 403 that arrives anyway (a role lost mid-session) turns it off for good.
  const canManageKyb = roleResolved && canManage && !kybForbidden
  const showKybStart =
    canManageKyb &&
    !kybLoading &&
    !kybUnavailable &&
    !legalFormNeeded &&
    (kybCardState === 'none' || kybCardState === 'in_progress' || kybCardState === 'retry')
  // Terminal results do not change on their own, so they get no refresh button.
  const kybTerminal = kybCardState === 'approved' || kybCardState === 'rejected'
  const showKybRecheck = !kybLoading && (kybUnavailable || !kybTerminal)
  // Shown on the unavailable path too: a MEMBER whose read was refused would
  // otherwise be left with a retry button and no explanation.
  const showKybMemberNotice =
    !kybLoading && !kybTerminal && ((roleResolved && !canManage) || kybForbidden)

  return (
    <div className="space-y-6">
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 border border-green-500 bg-green-500/20 px-4 py-3 text-green-500 dark:text-green-400"
        >
          <CheckCircle size={18} />
          <span className="font-plus-jakarta text-[14px]">{successMessage}</span>
        </motion.div>
      )}

      {actionError && <OrgErrorBanner message={actionError} />}

      {/* Organization overview */}
      <div className={cn(ORG_CARD, cardPadding)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className={ORG_HEADING}>{organization.name}</h2>
            <p className={cn('mt-1', ORG_SUBHEADING)}>
              {organization.description || `@${organization.slug}`}
            </p>
          </div>
          {role && (
            <div className="shrink-0">
              <p className={ORG_LABEL}>Your role</p>
              <RoleBadge role={role} className="mt-1" />
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className={cn(ORG_SURFACE, 'px-4 py-3')}>
            <p className={ORG_LABEL}>Members</p>
            <p className="mt-1 font-plus-jakarta text-[20px] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {listLoading ? '—' : membersTotal}
            </p>
          </div>
          {canManage && (
            <div className={cn(ORG_SURFACE, 'px-4 py-3')}>
              <p className={ORG_LABEL}>Total inquiries</p>
              <p className="mt-1 font-plus-jakarta text-[20px] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                {summary?.totals?.total ?? (summaryLoading ? '—' : 0)}
              </p>
            </div>
          )}
          <div className={cn(ORG_SURFACE, 'px-4 py-3')}>
            <p className={ORG_LABEL}>Created</p>
            <p className="mt-1 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatDate(organization.created_at)}
            </p>
          </div>
        </div>

        {/*
          Email-domain claim. Three distinct states, never collapsed into two:
          an unverified claim is only a string an administrator typed, so it must
          not be presented with any of the affirmative styling that "verified"
          gets. `email_domain_verified` is the only field that may drive that.
        */}
        <div className={cn(ORG_SURFACE, 'mt-3 flex items-start gap-3 px-4 py-3')}>
          <DomainIcon size={18} className={cn('mt-[2px] shrink-0', domainCopy.iconClass)} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                {domainCopy.title}
              </p>
              <DomainStatusBadge state={domainState} />
            </div>
            <p className={cn('mt-1', ORG_SUBHEADING)}>{domainCopy.description}</p>
          </div>
        </div>

        {/*
          Business verification (KYB). Six distinct states, never collapsed:
          "under review" is not "verified", and "action needed" is not
          "rejected". Only `moderation_comment` may ever be shown to a user —
          Sumsub's `clientComment` is internal and stays on the server.
        */}
        <div className={cn(ORG_SURFACE, 'mt-3 flex items-start gap-3 px-4 py-3')}>
          <KybIcon
            size={18}
            className={cn(
              'mt-[2px] shrink-0',
              kybUnavailable ? neutralIconClass : kybCopy.iconClass,
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                Business verification
              </p>
              {/* No badge while loading or unavailable: an unknown status must
                  not be labelled with any of the six. */}
              {!kybLoading && !kybUnavailable && <KybStatusBadge state={kybCardState} />}
            </div>
            <p className={cn('mt-1', ORG_SUBHEADING)}>
              {kybLoading
                ? 'Checking the current status…'
                : kybUnavailable
                  ? 'We could not load the verification status. This says nothing about the organization itself — try again in a moment.'
                  : kybCopy.description}
            </p>

            {kybComment && (
              <p className={cn('mt-2', ORG_SUBHEADING)}>Note from the review: {kybComment}</p>
            )}

            {showKybMemberNotice && (
              <p className={cn('mt-2', ORG_SUBHEADING)}>
                An organization owner or admin completes this step.
              </p>
            )}

            {legalFormNeeded && canManageKyb && (
              <div className="mt-3">
                <p className={ORG_SUBHEADING}>
                  Tell us the organization&apos;s legal form first — it decides which checks run,
                  and it cannot be changed once verification has started.
                </p>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Select value={legalForm} onValueChange={setLegalForm}>
                    <SelectTrigger
                      id="org-kyb-legal-form"
                      aria-label="Legal form"
                      className={cn(
                        ORG_SELECT_TRIGGER,
                        '!h-10 font-plus-jakarta text-[14px] sm:w-[260px]',
                      )}
                    >
                      <SelectValue placeholder="Select legal form" />
                    </SelectTrigger>
                    <SelectContent className={ORG_SELECT_CONTENT}>
                      {LEGAL_FORMS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className={ORG_SELECT_ITEM}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleSaveLegalForm}
                    isLoading={savingLegalForm || kybMinting}
                    disabled={!legalForm}
                  >
                    Save and start
                  </Button>
                </div>
              </div>
            )}

            {kybLink && (
              <p className={cn('mt-2', ORG_SUBHEADING)}>
                <a
                  href={kybLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Open the verification page
                </a>{' '}
                — the link is single-use and expires. Come back and check again when you are done.
              </p>
            )}

            {kybActionError && (
              <p className="mt-2 font-plus-jakarta text-[14px] leading-[20px] text-red-500 dark:text-red-400">
                {kybActionError}
              </p>
            )}

            {(showKybStart || showKybRecheck) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {showKybStart && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleStartKyb}
                    isLoading={kybMinting}
                  >
                    {kybCardState === 'none' ? 'Start verification' : 'Resume verification'}
                  </Button>
                )}
                {showKybRecheck && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRecheckKyb}
                    isLoading={kybChecking}
                  >
                    Check again
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/*
          Banking credentials are write-only server-side: the API returns
          `bank_account_number_set` / `bank_routing_number_set` booleans instead of
          the values, so all we can ever show is whether something is on file.
        */}
        {profile && (
          <div className={cn(ORG_SURFACE, 'mt-3 flex items-center gap-3 px-4 py-3')}>
            <Landmark
              size={18}
              className="shrink-0 text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]"
            />
            <div className="min-w-0">
              <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                {hasBankingOnFile ? 'Banking details on file' : 'No banking details on file'}
              </p>
              <p className={ORG_SUBHEADING}>
                {hasBankingOnFile
                  ? 'Account and routing numbers are stored encrypted and are never shown again. Re-enter them to replace what is on file.'
                  : 'Add an account and routing number to receive settlement.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Members */}
      <div className={cn(ORG_CARD, cardPadding)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className={ORG_HEADING}>Members</h2>
            <p className={cn('mt-1', ORG_SUBHEADING)}>
              {canManage
                ? 'Manage who is in your organization and review their activity.'
                : 'Everyone in your organization.'}
            </p>
          </div>
          {canManage && (
            <Button
              variant="primary"
              leftIcon={<UserPlus size={16} />}
              onClick={() => {
                // Clear any error left over from a previous attempt, otherwise the
                // mutation's last error would greet the user on reopen.
                inviteMutation.reset()
                setInviteOpen(true)
              }}
              className="shrink-0"
            >
              Invite member
            </Button>
          )}
        </div>

        {listError && <OrgErrorBanner message={listError} className="mt-4" />}

        <div className="mt-4">
          <OrgMembersList
            isMobile={isMobile}
            rows={rows}
            isLoading={listLoading}
            canManage={canManage}
            canChangeRoles={canChangeRoles}
            viewerRole={role}
            selectedUserId={selectedMember?.user_id ?? null}
            pendingUserId={pendingUserId}
            onViewActivity={toggleActivity}
            onChangeRole={handleChangeRole}
            onRemove={(row) => setMemberToRemove(row.membership)}
          />
        </div>

        {totalPages > 1 && (
          <PaginationWrapper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              setMembersOffset((page - 1) * MEMBERS_PAGE_LIMIT)
              setSelectedMember(null)
            }}
            className="mt-4"
          />
        )}
      </div>

      {/* Per-member activity (managers only) */}
      {canManage && selectedMember && (
        <MemberInquiriesPanel
          orgId={orgId}
          member={selectedMember}
          isMobile={isMobile}
          onClose={() => setSelectedMember(null)}
        />
      )}

      {/* Pending invitations (managers only) */}
      {canManage && (
        <div className={cn(ORG_CARD, cardPadding)}>
          <h2 className={ORG_HEADING}>Pending invitations</h2>
          <p className={cn('mt-1', ORG_SUBHEADING)}>
            People who have been invited but have not joined yet.
          </p>
          <div className="mt-4">
            <OrgInvitationsList
              invitations={pendingInvitations}
              isLoading={invitationsLoading}
              pendingInviteId={revokeMutation.isPending ? inviteToRevoke?.id ?? null : null}
              onRevoke={setInviteToRevoke}
            />
          </div>
        </div>
      )}

      <InviteMemberModal
        isOpen={isInviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInvite}
        canInviteOwner={canInviteOwner}
        isSubmitting={inviteMutation.isPending}
        submitError={
          inviteMutation.error
            ? errorMessage(inviteMutation.error, 'Failed to send invitation')
            : null
        }
      />

      <ConfirmationModal
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleRemoveConfirmed}
        title="Remove member"
        message={
          memberToRemove
            ? `Remove ${memberName(memberToRemove)} from ${organization.name}? They will lose access to the organization. Their own bids and RFQs are not deleted.`
            : ''
        }
        confirmText="Remove"
        variant="danger"
        loading={removeMutation.isPending}
      />

      <ConfirmationModal
        isOpen={!!inviteToRevoke}
        onClose={() => setInviteToRevoke(null)}
        onConfirm={handleRevokeConfirmed}
        title="Revoke invitation"
        message={
          inviteToRevoke
            ? `Revoke the invitation sent to ${inviteToRevoke.email}? Their invite link will stop working.`
            : ''
        }
        confirmText="Revoke"
        variant="danger"
        loading={revokeMutation.isPending}
      />
    </div>
  )
}

export default OrganizationSection
